import React, { useLayoutEffect, useRef, useState } from 'react';

import { TEXT_FADE_RIGHT } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export function isElementOverflowing(element: HTMLElement): boolean {
  return element.scrollWidth > element.clientWidth + 1;
}

export function useOverflowFade<T extends HTMLElement>(): {
  ref: React.RefObject<T | null>;
  isOverflowing: boolean;
} {
  const ref = useRef<T>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = (): void => {
      const next = isElementOverflowing(element);
      setIsOverflowing((current) => (current === next ? current : next));
    };

    update();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    resizeObserver?.observe(element);

    const mutationObserver =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null;
    mutationObserver?.observe(element, {
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);

  return { ref, isOverflowing };
}

interface OverflowFadeTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

// GOTCHA: pass `block` in `className`. The rendered element is a <span>, and
// `clientWidth`/`scrollWidth` are BOTH 0 on a default `display: inline`
// element in every browser — so `isElementOverflowing` always reads
// `0 > 0 + 1` = false, and the fade never activates, no matter how long the
// text is. Every existing caller (app-sidebar, UserMenuHeader, RouteBreadcrumb)
// already adds `block`; it happens to look optional because a direct child of
// a `display:flex` parent gets auto-blockified and works by accident even
// without it — which is exactly what silently masked this for a while.
// Verified live: a post title inside a plain (non-flex) wrapper div rendered
// at its full un-clipped width and physically overlapped its sibling column,
// with scrollWidth/clientWidth both reading 0 the whole time.

export function OverflowFadeText({ children, className, ...props }: OverflowFadeTextProps) {
  const { ref, isOverflowing } = useOverflowFade<HTMLSpanElement>();

  return (
    <span
      ref={ref}
      className={cn('min-w-0 whitespace-nowrap', isOverflowing && TEXT_FADE_RIGHT, className)}
      {...props}
    >
      {children}
    </span>
  );
}
